import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, LogOutIcon, CheckCircleIcon, XCircleIcon, ClockIcon, EditIcon, SaveIcon, SendIcon, GlobeIcon, GithubIcon, TwitterIcon, LinkedinIcon, MapPinIcon, PlusIcon, TrashIcon, LinkIcon, AwardIcon, FolderIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { TextArea } from '../../components/ui/TextArea';
interface Skill {
  name: string;
  certificationUrl?: string;
}
interface Project {
  title: string;
  url: string;
  description?: string;
}
interface DeveloperProfile {
  name: string;
  title: string;
  bio: string;
  skills: Skill[];
  projects: Project[];
  openTo: string[];
  location: string;
  country: string;
  rateMin: number;
  rateMax: number;
  rateCurrency: string;
  website: string;
  github: string;
  twitter: string;
  linkedin: string;
  avatar: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  availability: 'available' | 'unavailable';
  rejectionReason?: string;
}
const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState<DeveloperProfile | null>(null);
  const [formData, setFormData] = useState<DeveloperProfile>({
    name: '',
    title: '',
    bio: '',
    skills: [],
    projects: [],
    openTo: [],
    location: '',
    country: '',
    rateMin: 50,
    rateMax: 100,
    rateCurrency: 'USD',
    website: '',
    github: '',
    twitter: '',
    linkedin: '',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    status: 'draft',
    availability: 'available'
  });
  const [newSkill, setNewSkill] = useState({
    name: '',
    certificationUrl: ''
  });
  const [newProject, setNewProject] = useState({
    title: '',
    url: '',
    description: ''
  });
  const [avatarUploadMethod, setAvatarUploadMethod] = useState<'upload' | 'url'>('upload');
  useEffect(() => {
    // Check if user is authenticated
    const isAuth = localStorage.getItem('developerAuth');
    if (!isAuth) {
      navigate('/developer/login');
      return;
    }
    // Load profile from localStorage
    const savedProfile = localStorage.getItem('developerProfile');
    if (savedProfile) {
      const parsedProfile = JSON.parse(savedProfile);
      setProfile(parsedProfile);
      setFormData(parsedProfile);
    } else {
      // New user - set name from signup
      const name = localStorage.getItem('developerName') || '';
      setFormData(prev => ({
        ...prev,
        name
      }));
      setIsEditing(true);
    }
  }, [navigate]);
  const handleLogout = () => {
    localStorage.removeItem('developerAuth');
    localStorage.removeItem('developerEmail');
    localStorage.removeItem('developerName');
    navigate('/developer/login');
  };
  const handleSaveDraft = () => {
    const updatedProfile = {
      ...formData,
      status: 'draft' as const
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setIsEditing(false);
  };
  const handleSubmitForApproval = () => {
    if (!formData.name || !formData.title || !formData.bio) {
      alert('Please fill in all required fields (Name, Title, Bio)');
      return;
    }
    if (formData.skills.length === 0) {
      alert('Please add at least one skill with certification');
      return;
    }
    const updatedProfile = {
      ...formData,
      status: 'pending' as const
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setIsEditing(false);
  };
  const handleToggleAvailability = () => {
    if (!profile) return;
    const newAvailability = profile.availability === 'available' ? 'unavailable' : 'available';
    const updatedProfile = {
      ...profile,
      availability: newAvailability
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setFormData(updatedProfile);
  };
  const handleAddSkill = () => {
    if (newSkill.name.trim() && !formData.skills.some(s => s.name === newSkill.name.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, {
          name: newSkill.name.trim(),
          certificationUrl: newSkill.certificationUrl.trim() || undefined
        }]
      });
      setNewSkill({
        name: '',
        certificationUrl: ''
      });
    }
  };
  const handleRemoveSkill = (skillName: string) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(s => s.name !== skillName)
    });
  };
  const handleAddProject = () => {
    if (newProject.title.trim() && newProject.url.trim()) {
      setFormData({
        ...formData,
        projects: [...formData.projects, {
          title: newProject.title.trim(),
          url: newProject.url.trim(),
          description: newProject.description.trim() || undefined
        }]
      });
      setNewProject({
        title: '',
        url: '',
        description: ''
      });
    }
  };
  const handleRemoveProject = (index: number) => {
    setFormData({
      ...formData,
      projects: formData.projects.filter((_, i) => i !== index)
    });
  };
  const handleOpenToChange = (option: string) => {
    if (formData.openTo.includes(option)) {
      setFormData({
        ...formData,
        openTo: formData.openTo.filter(o => o !== option)
      });
    } else {
      setFormData({
        ...formData,
        openTo: [...formData.openTo, option]
      });
    }
  };
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          avatar: reader.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className="px-3 py-1 bg-gray-800 text-gray-300 text-sm font-medium rounded-full border border-gray-700" data-id="element-360">
            Draft
          </span>;
      case 'pending':
        return <span className="px-3 py-1 bg-yellow-500/10 text-yellow-400 text-sm font-medium rounded-full flex items-center border border-yellow-500/20" data-id="element-361">
            <ClockIcon className="h-4 w-4 mr-1" data-id="element-362" />
            Pending Review
          </span>;
      case 'approved':
        return <span className="px-3 py-1 bg-green-500/10 text-green-400 text-sm font-medium rounded-full flex items-center border border-green-500/20" data-id="element-363">
            <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-364" />
            Approved
          </span>;
      case 'rejected':
        return <span className="px-3 py-1 bg-red-500/10 text-red-400 text-sm font-medium rounded-full flex items-center border border-red-500/20" data-id="element-365">
            <XCircleIcon className="h-4 w-4 mr-1" data-id="element-366" />
            Rejected
          </span>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-[#0F1115] relative" data-id="element-367">
      {/* Global Grain Texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 bg-grain mix-blend-overlay" data-id="element-368"></div>

      {/* Header */}
      <header className="bg-[#1a1d24] border-b border-gray-800 sticky top-0 z-10 shadow-lg" data-id="element-369">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-370">
          <div className="flex justify-between items-center h-16" data-id="element-371">
            <div className="flex items-center gap-3" data-id="element-372">
              <div className="w-10 h-10 bg-[#d73b3e] rounded-xl flex items-center justify-center shadow-lg" data-id="element-373">
                <UserIcon className="h-5 w-5 text-white" data-id="element-374" />
              </div>
              <span className="text-sm font-medium text-gray-400" data-id="element-375">
                Dashboard
              </span>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5" data-id="element-376">
              <LogOutIcon className="h-4 w-4" data-id="element-377" />
              <span data-id="element-378">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10" data-id="element-379">
        {/* Page Title with Availability Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-8" data-id="element-380">
          <div data-id="element-381">
            <h1 className="text-3xl font-bold text-white mb-1" data-id="element-382">
              Developer Dashboard
            </h1>
            <p className="text-sm text-gray-400" data-id="element-383">
              Manage your profile and availability
            </p>
          </div>
          {profile && <div className="flex items-center gap-3 bg-[#1a1d24] px-4 py-3 rounded-xl border border-gray-800" data-id="element-384">
              <span className="text-sm font-medium text-gray-400" data-id="element-385">
                Availability
              </span>
              <button onClick={handleToggleAvailability} className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-offset-2 focus:ring-offset-[#0F1115] ${profile.availability === 'available' ? 'bg-[#d73b3e]' : 'bg-gray-700'}`} data-id="element-386">
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${profile.availability === 'available' ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-387" />
              </button>
              <span className={`text-sm font-medium min-w-[80px] ${profile.availability === 'available' ? 'text-green-400' : 'text-gray-400'}`} data-id="element-388">
                {profile.availability === 'available' ? 'Available' : 'Unavailable'}
              </span>
            </div>}
        </div>

        {/* Status Banner */}
        {profile && <div className="mb-8" data-id="element-389">
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-6 shadow-lg" data-id="element-390">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-391">
                <div className="flex items-center gap-4" data-id="element-392">
                  <img src={profile.avatar} alt={profile.name} className="w-16 h-16 rounded-xl object-cover border-2 border-[#d73b3e] shadow-lg" data-id="element-393" />
                  <div data-id="element-394">
                    <h2 className="text-xl font-bold text-white mb-1" data-id="element-395">
                      {profile.name}
                    </h2>
                    <p className="text-sm text-gray-400" data-id="element-396">
                      {profile.title || 'No title set'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-397">
                  {getStatusBadge(profile.status)}
                </div>
              </div>
            </div>
          </div>}

        {/* Alert Messages */}
        {profile?.status === 'rejected' && profile.rejectionReason && <div className="mb-8" data-id="element-398">
            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6" data-id="element-399">
              <div className="flex gap-4" data-id="element-400">
                <div className="flex-shrink-0" data-id="element-401">
                  <XCircleIcon className="h-6 w-6 text-red-400" data-id="element-402" />
                </div>
                <div className="flex-1" data-id="element-403">
                  <h3 className="text-base font-semibold text-red-400 mb-2" data-id="element-404">
                    Profile Rejected
                  </h3>
                  <p className="text-sm text-red-300 leading-relaxed mb-3" data-id="element-405">
                    {profile.rejectionReason}
                  </p>
                  <p className="text-sm text-red-400" data-id="element-406">
                    Please update your profile and resubmit for approval.
                  </p>
                </div>
              </div>
            </div>
          </div>}

        {profile?.status === 'pending' && <div className="mb-8" data-id="element-407">
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-6" data-id="element-408">
              <div className="flex gap-4" data-id="element-409">
                <div className="flex-shrink-0" data-id="element-410">
                  <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-411" />
                </div>
                <div className="flex-1" data-id="element-412">
                  <h3 className="text-base font-semibold text-yellow-400 mb-2" data-id="element-413">
                    Profile Under Review
                  </h3>
                  <p className="text-sm text-yellow-300 leading-relaxed" data-id="element-414">
                    Your profile has been submitted and is currently being
                    reviewed by our team. You will be notified once the review
                    is complete.
                  </p>
                </div>
              </div>
            </div>
          </div>}

        {/* Profile Form */}
        <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 shadow-lg" data-id="element-415">
          <div className="flex items-center justify-between p-6 border-b border-gray-800" data-id="element-416">
            <div data-id="element-417">
              <h3 className="text-lg font-semibold text-white mb-1" data-id="element-418">
                Profile Information
              </h3>
              <p className="text-sm text-gray-400" data-id="element-419">
                {isEditing ? 'Update your profile details' : 'View your profile information'}
              </p>
            </div>
            {profile && !isEditing && profile.status !== 'pending' && <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20" data-id="element-420">
                <EditIcon className="h-4 w-4" data-id="element-421" />
                <span className="text-sm font-medium" data-id="element-422">Edit</span>
              </button>}
          </div>

          <div className="p-6" data-id="element-423">
            {isEditing ? <div className="space-y-8" data-id="element-424">
                {/* Avatar Section */}
                <div data-id="element-425">
                  <label className="block text-sm font-semibold text-gray-300 mb-4" data-id="element-426">
                    Profile Picture <span className="text-[#d73b3e]" data-id="element-427">*</span>
                  </label>
                  <div className="flex flex-col sm:flex-row items-start gap-6" data-id="element-428">
                    <img src={formData.avatar} alt="Avatar preview" className="w-24 h-24 rounded-xl object-cover border-2 border-gray-700 shadow-lg" data-id="element-429" />
                    <div className="flex-1 w-full" data-id="element-430">
                      <div className="flex gap-2 mb-4" data-id="element-431">
                        <button type="button" onClick={() => setAvatarUploadMethod('upload')} className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${avatarUploadMethod === 'upload' ? 'bg-[#d73b3e] text-white shadow-lg' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-432">
                          Upload File
                        </button>
                        <button type="button" onClick={() => setAvatarUploadMethod('url')} className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${avatarUploadMethod === 'url' ? 'bg-[#d73b3e] text-white shadow-lg' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-433">
                          Enter URL
                        </button>
                      </div>
                      {avatarUploadMethod === 'upload' ? <div data-id="element-434">
                          <input type="file" accept="image/*" onChange={handleAvatarUpload} className="block w-full text-sm text-gray-400
                              file:mr-4 file:py-2.5 file:px-4
                              file:rounded-xl file:border-0
                              file:text-sm file:font-medium
                              file:bg-[#d73b3e] file:text-white
                              hover:file:bg-[#c02f32] file:transition-colors
                              cursor-pointer" data-id="element-435" />
                          <p className="text-xs text-gray-500 mt-2" data-id="element-436">
                            Max 5MB • JPG, PNG, or GIF
                          </p>
                        </div> : <div data-id="element-437">
                          <input value={formData.avatar} onChange={e => setFormData({
                      ...formData,
                      avatar: e.target.value
                    })} placeholder="https://example.com/avatar.jpg" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-438" />
                          <p className="text-xs text-gray-500 mt-2" data-id="element-439">
                            Enter a direct link to your profile picture
                          </p>
                        </div>}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-800" data-id="element-440"></div>

                {/* Basic Information */}
                <div data-id="element-441">
                  <h4 className="text-sm font-semibold text-gray-300 mb-4" data-id="element-442">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-443">
                    <div data-id="element-444">
                      <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-445">
                        Full Name <span className="text-[#d73b3e]" data-id="element-446">*</span>
                      </label>
                      <input value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} placeholder="John Doe" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-447" />
                    </div>
                    <div data-id="element-448">
                      <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-449">
                        Professional Title{' '}
                        <span className="text-[#d73b3e]" data-id="element-450">*</span>
                      </label>
                      <input value={formData.title} onChange={e => setFormData({
                    ...formData,
                    title: e.target.value
                  })} placeholder="Senior Laravel Developer" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-451" />
                    </div>
                  </div>
                </div>

                <div data-id="element-452">
                  <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-453">
                    Bio <span className="text-[#d73b3e]" data-id="element-454">*</span>
                  </label>
                  <textarea value={formData.bio} onChange={e => setFormData({
                ...formData,
                bio: e.target.value
              })} placeholder="Tell us about your experience and expertise..." rows={4} className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all resize-vertical" data-id="element-455" />
                </div>

                {/* Divider */}
                <div className="border-t border-gray-800" data-id="element-456"></div>

                {/* Location */}
                <div data-id="element-457">
                  <h4 className="text-sm font-semibold text-gray-300 mb-4" data-id="element-458">
                    Location
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-459">
                    <div data-id="element-460">
                      <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-461">
                        City
                      </label>
                      <input value={formData.location} onChange={e => setFormData({
                    ...formData,
                    location: e.target.value
                  })} placeholder="San Francisco" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-462" />
                    </div>
                    <div data-id="element-463">
                      <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-464">
                        Country
                      </label>
                      <input value={formData.country} onChange={e => setFormData({
                    ...formData,
                    country: e.target.value
                  })} placeholder="United States" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-465" />
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-800" data-id="element-466"></div>

                {/* Rate */}
                <div data-id="element-467">
                  <h4 className="text-sm font-semibold text-gray-300 mb-4" data-id="element-468">
                    Hourly Rate
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-469">
                    <div data-id="element-470">
                      <label className="block text-xs font-medium text-gray-500 mb-2" data-id="element-471">
                        Minimum
                      </label>
                      <input type="number" value={formData.rateMin} onChange={e => setFormData({
                    ...formData,
                    rateMin: Number(e.target.value)
                  })} placeholder="50" min="0" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-472" />
                    </div>
                    <div data-id="element-473">
                      <label className="block text-xs font-medium text-gray-500 mb-2" data-id="element-474">
                        Maximum
                      </label>
                      <input type="number" value={formData.rateMax} onChange={e => setFormData({
                    ...formData,
                    rateMax: Number(e.target.value)
                  })} placeholder="100" min="0" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-475" />
                    </div>
                    <div data-id="element-476">
                      <label className="block text-xs font-medium text-gray-500 mb-2" data-id="element-477">
                        Currency
                      </label>
                      <select value={formData.rateCurrency} onChange={e => setFormData({
                    ...formData,
                    rateCurrency: e.target.value
                  })} className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-478">
                        <option value="USD" data-id="element-479">USD</option>
                        <option value="EUR" data-id="element-480">EUR</option>
                        <option value="GBP" data-id="element-481">GBP</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-800" data-id="element-482"></div>

                {/* Skills */}
                <div data-id="element-483">
                  <h4 className="text-sm font-semibold text-gray-300 mb-4" data-id="element-484">
                    Skills & Certifications{' '}
                    <span className="text-[#d73b3e]" data-id="element-485">*</span>
                  </h4>
                  <div className="space-y-4 mb-4" data-id="element-486">
                    <input value={newSkill.name} onChange={e => setNewSkill({
                  ...newSkill,
                  name: e.target.value
                })} placeholder="Skill name (e.g., Laravel, PHP, Vue.js)" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-487" />
                    <div className="flex gap-3" data-id="element-488">
                      <input value={newSkill.certificationUrl} onChange={e => setNewSkill({
                    ...newSkill,
                    certificationUrl: e.target.value
                  })} placeholder="Certification URL (optional)" className="flex-1 px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-489" />
                      <button type="button" onClick={handleAddSkill} className="px-6 py-3 bg-[#d73b3e] text-white rounded-xl font-medium hover:bg-[#c02f32] transition-all duration-300 flex items-center gap-2 shadow-lg" data-id="element-490">
                        <PlusIcon className="h-4 w-4" data-id="element-491" />
                        <span data-id="element-492">Add</span>
                      </button>
                    </div>
                  </div>
                  {formData.skills.length > 0 && <div className="space-y-2" data-id="element-493">
                      {formData.skills.map(skill => <div key={skill.name} className="flex items-center justify-between p-4 bg-[#d73b3e]/10 rounded-xl border border-[#d73b3e]/20" data-id="element-494">
                          <div className="flex items-center gap-3 flex-1" data-id="element-495">
                            <span className="font-medium text-[#d73b3e]" data-id="element-496">
                              {skill.name}
                            </span>
                            {skill.certificationUrl && <a href={skill.certificationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#d73b3e] hover:text-[#c02f32] text-sm transition-colors" data-id="element-497">
                                <AwardIcon className="h-3.5 w-3.5" data-id="element-498" />
                                <span data-id="element-499">Certificate</span>
                              </a>}
                          </div>
                          <button type="button" onClick={() => handleRemoveSkill(skill.name)} className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded-lg transition-all" data-id="element-500">
                            <TrashIcon className="h-4 w-4" data-id="element-501" />
                          </button>
                        </div>)}
                    </div>}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-800" data-id="element-502"></div>

                {/* Projects */}
                <div data-id="element-503">
                  <h4 className="text-sm font-semibold text-gray-300 mb-4" data-id="element-504">
                    Projects
                  </h4>
                  <div className="space-y-4 mb-4" data-id="element-505">
                    <input value={newProject.title} onChange={e => setNewProject({
                  ...newProject,
                  title: e.target.value
                })} placeholder="Project title" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-506" />
                    <input value={newProject.url} onChange={e => setNewProject({
                  ...newProject,
                  url: e.target.value
                })} placeholder="Project URL" className="w-full px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-507" />
                    <div className="flex gap-3" data-id="element-508">
                      <input value={newProject.description} onChange={e => setNewProject({
                    ...newProject,
                    description: e.target.value
                  })} placeholder="Brief description (optional)" className="flex-1 px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-509" />
                      <button type="button" onClick={handleAddProject} className="px-6 py-3 bg-[#d73b3e] text-white rounded-xl font-medium hover:bg-[#c02f32] transition-all duration-300 flex items-center gap-2 shadow-lg" data-id="element-510">
                        <PlusIcon className="h-4 w-4" data-id="element-511" />
                        <span data-id="element-512">Add</span>
                      </button>
                    </div>
                  </div>
                  {formData.projects.length > 0 && <div className="space-y-3" data-id="element-513">
                      {formData.projects.map((project, index) => <div key={index} className="flex items-start justify-between p-4 bg-white/5 rounded-xl border border-gray-800" data-id="element-514">
                          <div className="flex-1" data-id="element-515">
                            <div className="flex items-center gap-2 mb-2" data-id="element-516">
                              <FolderIcon className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-517" />
                              <span className="font-medium text-white" data-id="element-518">
                                {project.title}
                              </span>
                            </div>
                            <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#d73b3e] hover:text-[#c02f32] flex items-center gap-1 mb-2 transition-colors" data-id="element-519">
                              <LinkIcon className="h-3 w-3" data-id="element-520" />
                              <span className="truncate" data-id="element-521">{project.url}</span>
                            </a>
                            {project.description && <p className="text-sm text-gray-400 leading-relaxed" data-id="element-522">
                                {project.description}
                              </p>}
                          </div>
                          <button type="button" onClick={() => handleRemoveProject(index)} className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded-lg transition-all ml-4 flex-shrink-0" data-id="element-523">
                            <TrashIcon className="h-4 w-4" data-id="element-524" />
                          </button>
                        </div>)}
                    </div>}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-800" data-id="element-525"></div>

                {/* Open To */}
                <div data-id="element-526">
                  <h4 className="text-sm font-semibold text-gray-300 mb-4" data-id="element-527">
                    Work Preferences
                  </h4>
                  <div className="flex flex-wrap gap-3" data-id="element-528">
                    {['Full-time', 'Part-time', 'Contract', 'Freelance', 'Remote', 'Unavailable'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer px-4 py-2.5 rounded-xl bg-[#0F1115] border border-gray-700 hover:border-gray-600 transition-colors" data-id="element-529">
                        <input type="checkbox" checked={formData.openTo.includes(option)} onChange={() => handleOpenToChange(option)} className="h-4 w-4 text-[#d73b3e] focus:ring-[#d73b3e] border-gray-600 rounded bg-[#0F1115]" data-id="element-530" />
                        <span className="text-sm text-gray-300 font-medium" data-id="element-531">
                          {option}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-800" data-id="element-532"></div>

                {/* Social Links */}
                <div data-id="element-533">
                  <h4 className="text-sm font-semibold text-gray-300 mb-4" data-id="element-534">
                    Social Links
                  </h4>
                  <div className="space-y-4" data-id="element-535">
                    <div className="flex items-center gap-3" data-id="element-536">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#0F1115] rounded-xl flex items-center justify-center border border-gray-700" data-id="element-537">
                        <GlobeIcon className="h-5 w-5 text-gray-400" data-id="element-538" />
                      </div>
                      <input value={formData.website} onChange={e => setFormData({
                    ...formData,
                    website: e.target.value
                  })} placeholder="https://yourwebsite.com" className="flex-1 px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-539" />
                    </div>
                    <div className="flex items-center gap-3" data-id="element-540">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#0F1115] rounded-xl flex items-center justify-center border border-gray-700" data-id="element-541">
                        <GithubIcon className="h-5 w-5 text-gray-400" data-id="element-542" />
                      </div>
                      <input value={formData.github} onChange={e => setFormData({
                    ...formData,
                    github: e.target.value
                  })} placeholder="https://github.com/yourusername" className="flex-1 px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-543" />
                    </div>
                    <div className="flex items-center gap-3" data-id="element-544">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#0F1115] rounded-xl flex items-center justify-center border border-gray-700" data-id="element-545">
                        <TwitterIcon className="h-5 w-5 text-gray-400" data-id="element-546" />
                      </div>
                      <input value={formData.twitter} onChange={e => setFormData({
                    ...formData,
                    twitter: e.target.value
                  })} placeholder="https://twitter.com/yourusername" className="flex-1 px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-547" />
                    </div>
                    <div className="flex items-center gap-3" data-id="element-548">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#0F1115] rounded-xl flex items-center justify-center border border-gray-700" data-id="element-549">
                        <LinkedinIcon className="h-5 w-5 text-gray-400" data-id="element-550" />
                      </div>
                      <input value={formData.linkedin} onChange={e => setFormData({
                    ...formData,
                    linkedin: e.target.value
                  })} placeholder="https://linkedin.com/in/yourusername" className="flex-1 px-4 py-3 bg-[#0F1115] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:border-[#d73b3e] transition-all" data-id="element-551" />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-800" data-id="element-552">
                  <button onClick={handleSaveDraft} className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-all duration-300 border border-white/20" data-id="element-553">
                    <SaveIcon className="h-4 w-4" data-id="element-554" />
                    <span data-id="element-555">Save as Draft</span>
                  </button>
                  <button onClick={handleSubmitForApproval} className="flex items-center justify-center gap-2 px-6 py-3 bg-[#d73b3e] text-white rounded-xl font-medium hover:bg-[#c02f32] transition-all duration-300 shadow-lg" data-id="element-556">
                    <SendIcon className="h-4 w-4" data-id="element-557" />
                    <span data-id="element-558">Submit for Approval</span>
                  </button>
                  {profile && <button onClick={() => {
                setFormData(profile);
                setIsEditing(false);
              }} className="px-6 py-3 text-gray-400 hover:text-white transition-colors rounded-xl hover:bg-white/5" data-id="element-559">
                      Cancel
                    </button>}
                </div>
              </div> : profile ? <div className="space-y-8" data-id="element-560">
                {/* View Mode with improved layout */}
                <div data-id="element-561">
                  <h4 className="text-sm font-semibold text-gray-400 mb-4" data-id="element-562">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-563">
                    <div data-id="element-564">
                      <label className="block text-xs font-medium text-gray-500 mb-2" data-id="element-565">
                        Name
                      </label>
                      <p className="text-white font-medium" data-id="element-566">{profile.name}</p>
                    </div>
                    <div data-id="element-567">
                      <label className="block text-xs font-medium text-gray-500 mb-2" data-id="element-568">
                        Title
                      </label>
                      <p className="text-white font-medium" data-id="element-569">{profile.title}</p>
                    </div>
                  </div>
                </div>

                <div data-id="element-570">
                  <label className="block text-xs font-medium text-gray-500 mb-2" data-id="element-571">
                    Bio
                  </label>
                  <p className="text-white leading-relaxed" data-id="element-572">{profile.bio}</p>
                </div>

                <div className="border-t border-gray-800" data-id="element-573"></div>

                <div data-id="element-574">
                  <h4 className="text-sm font-semibold text-gray-400 mb-4" data-id="element-575">
                    Location & Rate
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-576">
                    <div data-id="element-577">
                      <label className="block text-xs font-medium text-gray-500 mb-2" data-id="element-578">
                        Location
                      </label>
                      <p className="text-white flex items-center gap-2 font-medium" data-id="element-579">
                        <MapPinIcon className="h-4 w-4 text-gray-400" data-id="element-580" />
                        {profile.location}, {profile.country}
                      </p>
                    </div>
                    <div data-id="element-581">
                      <label className="block text-xs font-medium text-gray-500 mb-2" data-id="element-582">
                        Hourly Rate
                      </label>
                      <p className="text-white font-medium" data-id="element-583">
                        {profile.rateCurrency} ${profile.rateMin}-$
                        {profile.rateMax}/hour
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-800" data-id="element-584"></div>

                <div data-id="element-585">
                  <h4 className="text-sm font-semibold text-gray-400 mb-4" data-id="element-586">
                    Skills & Certifications
                  </h4>
                  <div className="space-y-2" data-id="element-587">
                    {profile.skills.map(skill => <div key={skill.name} className="flex items-center gap-3 p-3 bg-[#d73b3e]/10 rounded-xl border border-[#d73b3e]/20" data-id="element-588">
                        <span className="font-medium text-[#d73b3e]" data-id="element-589">
                          {skill.name}
                        </span>
                        {skill.certificationUrl && <a href={skill.certificationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#d73b3e] hover:text-[#c02f32] text-sm transition-colors" data-id="element-590">
                            <AwardIcon className="h-3.5 w-3.5" data-id="element-591" />
                            <span data-id="element-592">View Certificate</span>
                          </a>}
                      </div>)}
                  </div>
                </div>

                {profile.projects.length > 0 && <>
                    <div className="border-t border-gray-800" data-id="element-593"></div>
                    <div data-id="element-594">
                      <h4 className="text-sm font-semibold text-gray-400 mb-4" data-id="element-595">
                        Projects
                      </h4>
                      <div className="space-y-3" data-id="element-596">
                        {profile.projects.map((project, index) => <div key={index} className="p-4 bg-white/5 rounded-xl border border-gray-800" data-id="element-597">
                            <div className="flex items-center gap-2 mb-2" data-id="element-598">
                              <FolderIcon className="h-4 w-4 text-gray-400" data-id="element-599" />
                              <span className="font-medium text-white" data-id="element-600">
                                {project.title}
                              </span>
                            </div>
                            <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#d73b3e] hover:text-[#c02f32] flex items-center gap-1 mb-2 transition-colors" data-id="element-601">
                              <LinkIcon className="h-3 w-3" data-id="element-602" />
                              <span className="truncate" data-id="element-603">{project.url}</span>
                            </a>
                            {project.description && <p className="text-sm text-gray-400 leading-relaxed" data-id="element-604">
                                {project.description}
                              </p>}
                          </div>)}
                      </div>
                    </div>
                  </>}

                <div className="border-t border-gray-800" data-id="element-605"></div>

                <div data-id="element-606">
                  <h4 className="text-sm font-semibold text-gray-400 mb-4" data-id="element-607">
                    Work Preferences
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-608">
                    {profile.openTo.map(option => <span key={option} className="px-4 py-2 bg-white/5 text-gray-300 rounded-xl text-sm font-medium border border-gray-800" data-id="element-609">
                        {option}
                      </span>)}
                  </div>
                </div>

                {(profile.website || profile.github || profile.twitter || profile.linkedin) && <>
                    <div className="border-t border-gray-800" data-id="element-610"></div>
                    <div data-id="element-611">
                      <h4 className="text-sm font-semibold text-gray-400 mb-4" data-id="element-612">
                        Social Links
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-613">
                        {profile.website && <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors group" data-id="element-614">
                            <GlobeIcon className="h-5 w-5 text-gray-400 group-hover:text-[#d73b3e] transition-colors" data-id="element-615" />
                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors" data-id="element-616">
                              Website
                            </span>
                          </a>}
                        {profile.github && <a href={profile.github} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors group" data-id="element-617">
                            <GithubIcon className="h-5 w-5 text-gray-400 group-hover:text-[#d73b3e] transition-colors" data-id="element-618" />
                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors" data-id="element-619">
                              GitHub
                            </span>
                          </a>}
                        {profile.twitter && <a href={profile.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors group" data-id="element-620">
                            <TwitterIcon className="h-5 w-5 text-gray-400 group-hover:text-[#d73b3e] transition-colors" data-id="element-621" />
                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors" data-id="element-622">
                              Twitter
                            </span>
                          </a>}
                        {profile.linkedin && <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors group" data-id="element-623">
                            <LinkedinIcon className="h-5 w-5 text-gray-400 group-hover:text-[#d73b3e] transition-colors" data-id="element-624" />
                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors" data-id="element-625">
                              LinkedIn
                            </span>
                          </a>}
                      </div>
                    </div>
                  </>}
              </div> : <div className="text-center py-16" data-id="element-626">
                <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-627">
                  <UserIcon className="h-8 w-8 text-gray-600" data-id="element-628" />
                </div>
                <p className="text-gray-400 text-lg" data-id="element-629">No profile created yet</p>
                <p className="text-gray-500 text-sm mt-2" data-id="element-630">
                  Start by filling out your profile information above
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default DeveloperDashboard;